import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { QrCode } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { SectionCard } from '@/components/common/SectionCard';
import type { TotpSetupStart } from '@/api/auth.api';
import { TWO_FACTOR_SETUP_STRINGS as S } from '@/lib/constants/two-factor-strings';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { TotpQrCode } from './TotpQrCode';

export interface TotpScanCardProps {
  /** The setup-start query (`useTotpSetupStart`). */
  start: {
    data?: TotpSetupStart;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
}

/**
 * Step 1 of authenticator setup (MYN-241): the QR code plus the setup key for
 * manual entry. A failed start shows the API's reason with a retry; a failed
 * clipboard copy is reported and says how to proceed.
 */
export function TotpScanCard({ start }: TotpScanCardProps) {
  const setup = start.data;
  const [keyCopied, setKeyCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const handleCopyKey = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setKeyCopied(true);
      setCopyFailed(false);
    } catch (error) {
      // Clipboard can be blocked (permissions, insecure context): report it
      // and tell the user to type the key, which stays visible.
      Sentry.captureException(error);
      setKeyCopied(false);
      setCopyFailed(true);
    }
  };

  return (
    <SectionCard
      title={S.STEP_SCAN_TITLE}
      subtitle={S.STEP_SCAN_SUBTITLE}
      icon={<QrCode size={20} />}
    >
      {start.isPending && <Skeleton className="mx-auto h-[200px] w-[200px]" />}
      {start.isError && (
        <ErrorState
          description={getApiErrorMessage(start.error, S.LOAD_FAILED)}
          onRetry={() => void start.refetch()}
        />
      )}
      {setup && (
        <div className="flex flex-col items-center gap-4 py-2 sm:flex-row sm:items-start sm:gap-8">
          <TotpQrCode uri={setup.otpauthUri} />
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-slate m-0 text-sm">{S.MANUAL_LABEL}</p>
            <code className="bg-page-bg text-navy rounded-lg px-3 py-2 font-mono text-sm break-all">
              {setup.secret}
            </code>
            <Button
              type="button"
              variant="link"
              className="self-start text-sm"
              onClick={() => void handleCopyKey(setup.secret)}
            >
              {keyCopied ? S.KEY_COPIED : S.COPY_KEY}
            </Button>
            {copyFailed && (
              <p role="alert" className="text-danger m-0 text-sm">
                {S.KEY_COPY_FAILED}
              </p>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}
